import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { getEmailTemplates, saveEmailTemplate, sendEmailToAllUsers } from "../../api/EmailApi";
import { ApiStatus } from "../../api/ApiUtilities";
import ContainerCard from "../../common/CardContainer";
import ErrorDisplay from "../../common/ErrorDisplay";
import LoadingSpinner from "../../common/LoadingSpinner";
import PageHeader from "../../common/PageHeader";
import TextInput from "../../common/TextInput";
import { toast } from "../../common/notify";
import { EmailPlaceholders, EmailTemplate, EmailTemplateNames } from "../../data/EmailTemplate";

function PlaceholderLegend() {
  return (
    <details className="mb-2">
      <summary className="text-muted small">Available placeholders (replaced per recipient)</summary>
      <table className="table table-sm mt-2">
        <tbody>
          {EmailPlaceholders.map((p) => (
            <tr key={p.token}>
              <td>
                <code>{p.token}</code>
              </td>
              <td className="text-muted">{p.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

function EmailAdmin() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [editing, setEditing] = useState<EmailTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  // email-all state
  const [broadcastSubject, setBroadcastSubject] = useState("");
  const [broadcastContent, setBroadcastContent] = useState("");
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [sending, setSending] = useState(false);

  function loadTemplates() {
    setLoading(true);
    getEmailTemplates()
      .then((response) => {
        if (response.status === ApiStatus.Ok && response.data) {
          setTemplates(response.data);
          // prefill the broadcast composer from the AttendeesEmail template
          const attendees = response.data.find((t) => t.type === "AttendeesEmail");
          if (attendees) {
            setBroadcastSubject((current) => current || attendees.subject);
            setBroadcastContent((current) => current || attendees.contentHtml);
          }
        } else {
          setError(response.message || "Could not load email templates");
        }
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function saveTemplate() {
    if (!editing) return;
    setSaving(true);
    saveEmailTemplate(editing.type, editing)
      .then((response) => {
        if (response.status === ApiStatus.Ok) {
          toast.success("Template saved");
          setEditing(null);
          loadTemplates();
        } else {
          toast.error(response.message || "Could not save template");
        }
      })
      .catch((e: Error) => toast.error(`Could not save template: ${e.message}`))
      .finally(() => setSaving(false));
  }

  function sendToAll() {
    setSending(true);
    sendEmailToAllUsers(broadcastSubject, broadcastContent)
      .then((response) => {
        if (response.status === ApiStatus.Ok) {
          toast.success(response.message || `Email queued to ${response.data} attendees`);
          setShowSendConfirm(false);
        } else {
          toast.error(response.message || "Could not send the email");
        }
      })
      .catch((e: Error) => toast.error(`Could not send the email: ${e.message}`))
      .finally(() => setSending(false));
  }

  return (
    <div>
      <PageHeader title="Emails" subtitle="Edit the emails the system sends, and email all attendees" />
      <ErrorDisplay error={error} />
      {loading ? <LoadingSpinner /> : null}

      {editing ? (
        <ContainerCard heading={`Edit template — ${EmailTemplateNames[editing.type] ?? editing.type}`}>
          <TextInput name="Subject" label="Subject" type="text" placeholder="" value={editing.subject} error="" onChange={(value) => setEditing({ ...editing, subject: value })} />
          <PlaceholderLegend />
          <label className="fw-bold mt-2" htmlFor="templateHtml">
            Email content (HTML)
          </label>
          <textarea
            id="templateHtml"
            className="form-control font-monospace mb-2"
            rows={12}
            value={editing.contentHtml}
            onChange={(e) => setEditing({ ...editing, contentHtml: e.target.value })}
          />
          <label className="fw-bold" htmlFor="templatePlain">
            Plain text version (for email clients that can't show HTML)
          </label>
          <textarea
            id="templatePlain"
            className="form-control font-monospace mb-3"
            rows={6}
            value={editing.contentPlainText}
            onChange={(e) => setEditing({ ...editing, contentPlainText: e.target.value })}
          />
          <div className="mb-1 text-muted small">Preview:</div>
          <div className="border rounded p-3 mb-3" dangerouslySetInnerHTML={{ __html: editing.contentHtml }} />
          <div className="d-flex">
            <button type="button" className="btn btn-light" disabled={saving} onClick={() => setEditing(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary ms-auto" disabled={saving || !editing.subject || !editing.contentHtml} onClick={saveTemplate}>
              {saving ? "Saving..." : "Save template"}
            </button>
          </div>
          <p className="sl-no-email-note text-end mb-0">Saving does not send any email — it changes all future emails of this type.</p>
        </ContainerCard>
      ) : (
        <>
          <ContainerCard heading="Email templates">
            <p className="text-muted">These are the emails the system sends automatically. Edits apply to all emails sent after saving.</p>
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Template</th>
                    <th>Subject</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((template) => (
                    <tr key={template.type}>
                      <td>{EmailTemplateNames[template.type] ?? template.type}</td>
                      <td className="text-muted">{template.subject}</td>
                      <td className="text-end">
                        <button type="button" className="btn btn-light btn-sm" onClick={() => setEditing({ ...template })}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ContainerCard>

          <ContainerCard heading="Email all attendees">
            <p className="text-muted">
              Sends an email to <strong>every enabled user</strong> with an email address. Placeholders below are replaced per recipient. The default content comes from the "Email to all attendees"
              template above.
            </p>
            <TextInput name="BroadcastSubject" label="Subject" type="text" placeholder="" value={broadcastSubject} error="" onChange={setBroadcastSubject} />
            <PlaceholderLegend />
            <label className="fw-bold" htmlFor="broadcastContent">
              Email content (HTML)
            </label>
            <textarea id="broadcastContent" className="form-control font-monospace mb-3" rows={10} value={broadcastContent} onChange={(e) => setBroadcastContent(e.target.value)} />
            <div className="mb-1 text-muted small">Preview:</div>
            <div className="border rounded p-3 mb-3" dangerouslySetInnerHTML={{ __html: broadcastContent }} />
            <div className="d-flex">
              <button type="button" className="btn btn-primary ms-auto" disabled={sending || !broadcastSubject || !broadcastContent} onClick={() => setShowSendConfirm(true)}>
                Send to all attendees...
              </button>
            </div>
          </ContainerCard>
        </>
      )}

      <Modal show={showSendConfirm} onHide={() => setShowSendConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Email all attendees</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This will email <strong>every enabled user</strong> with the subject:
          </p>
          <p className="fw-bold mb-0">{broadcastSubject}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" className="me-auto" onClick={() => setShowSendConfirm(false)} disabled={sending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={sendToAll} disabled={sending}>
            {sending ? "Sending..." : "Send to all attendees"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default EmailAdmin;
